import React, { memo, useState } from 'react';
import {
  useFieldArray,
  UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';
import styles from '../../DataExtractionTemplate.module.scss';
import { DataExtractionTemplateState } from '../../DataExtractionTemplate';
import ConfirmationDialog from '../ConfirmationDialog';
import SectionTitle from '../SectionTitle';
import { useHidingDefaultFields } from '../../useHidingDefaultFields';
import AddOutcome from './AddOutcome/AddOutcome';
import outcomeStyles from './OutcomesPanel.module.scss';
import OutcomeEditor from './OutcomeEditor';
import { HiddenFieldCategory, OutcomeTypes } from 'types/DataExtraction';
import {
  Heading,
  Accordion,
  Text,
  PopoverTrigger,
  PopoverContent,
  Popover,
  FormControl,
  Checkbox,
} from 'components/core';
import { useReviewFeaturesContext } from 'contexts/ReviewFeaturesProvider';

type Props = {
  resultsDataAnchor?: string;
};
const OutcomesPanel = ({ resultsDataAnchor }: Props) => {
  const [modal, setModal] = useState<React.ReactElement | null>();
  const outcomes = useFieldArray<DataExtractionTemplateState>({
    name: 'outcomes.outcomes',
  }) as UseFieldArrayReturn<DataExtractionTemplateState, 'outcomes.outcomes'>;
  const { getValues, formState } = useFormContext();
  const { handleChange, isChecked } = useHidingDefaultFields(
    HiddenFieldCategory.OUTCOMES
  );
  const { hasSelectableMeasures } = useReviewFeaturesContext();

  const onAddOutcome = () => {
    outcomes.append({
      name: '',
      outcome_type: OutcomeTypes.CONTINUOUS,
      timepoints: [{ name: 'End of study' }],
      has_extracted_data: false,
    });
  };

  const outcomesPopoverText1 = hasSelectableMeasures
    ? 'continuous, dichotomous or qualitative'
    : 'continuous or dichotomous';

  const outcomesPopoverText2 = hasSelectableMeasures
    ? 'If you think an outcome could be continuous, dichotomous or qualitative, then we suggest to add multiple outcomes with different types. This will make it easier to analyse and synthesise the data later.'
    : 'If you need both, we suggest to add two different outcomes as this will make it easier to analyse and synthesise the data later.';

  const themesPopoverText = hasSelectableMeasures
    ? "If you're looking to extract data such as themes or evaluations, you can add qualitative outcomes."
    : "If you're looking to extract data for a qualitative outcome like themes or evaluations, you can add an outcome which is either continuous or dichotomous and use custom data types to extract qualitative data.";

  return (
    <section className={styles.Panel}>
      {modal}
      <SectionTitle
        title="Outcomes"
        pendoKey="det-outcomes"
        ariaLabel="Get help with outcomes"
      />
      <div className={outcomeStyles.outcomeDetailsSection}>
        <Heading type="h5" component="h4" variant="neutral">
          Outcomes
        </Heading>
        <Popover closeOnBlur={true} renderCloseButton={false}>
          <Text variant="weak">
            Add the{' '}
            <PopoverTrigger>
              <Text component="span" className={styles.PopoverTrigger}>
                main outcomes
              </Text>
            </PopoverTrigger>
            <PopoverContent className={styles.PopoverContentText}>
              <Text>
                Add the specific outcomes you are interested in investigating,
                which can be {outcomesPopoverText1}.
              </Text>
              <Text>{outcomesPopoverText2}</Text>
              <Text>{themesPopoverText}</Text>
            </PopoverContent>{' '}
            you plan to collect, based on your protocol. When extracting data
            from each study, you can select which outcomes are reported and add
            study specific sub-outcomes.
          </Text>
        </Popover>
        <div className={outcomeStyles.outcomesList}>
          <Accordion component="div" allowMultiple={false}>
            {outcomes.fields.map((field, index) => {
              return (
                <OutcomeEditor
                  key={field.id}
                  index={index}
                  resultsDataAnchor={resultsDataAnchor}
                  hasExtractedData={field.has_extracted_data}
                  onDelete={() => {
                    const { id, has_extracted_data } = getValues(
                      `outcomes.outcomes.${index}`
                    );

                    if (id && has_extracted_data) {
                      setModal(
                        <ConfirmationDialog
                          onConfirm={() => outcomes.remove(index)}
                          onClose={() => setModal(null)}
                        />
                      );
                    } else {
                      outcomes.remove(index);
                    }
                  }}
                />
              );
            })}
          </Accordion>

          <AddOutcome
            onClick={onAddOutcome}
            disabled={!formState.isValid}
            length={outcomes.fields.length}
          />
        </div>
      </div>
      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Outcome details
        </Heading>
        <Text variant="weak" className={outcomeStyles.outcomeDetailsText}>
          For each outcome reported in the study, what outcome details do you
          want to collect?
        </Text>
        <ul>
          <li>
            <FormControl>
              <Checkbox
                name="scale"
                value="scale"
                checked={isChecked('scale')}
                onChange={handleChange}
              >
                <Text variant="medium">Scale</Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl>
              <Checkbox
                name="range"
                value="range"
                checked={isChecked('range')}
                onChange={handleChange}
              >
                <Text variant="medium">Range</Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl>
              <Checkbox
                name="unit_of_measurement"
                value="unit_of_measurement"
                checked={isChecked('unit_of_measurement')}
                onChange={handleChange}
              >
                <Text variant="medium">Unit of measurement</Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl>
              <Checkbox
                name="direction"
                value="direction"
                checked={isChecked('direction')}
                onChange={handleChange}
              >
                <Text variant="medium">Direction</Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl>
              <Checkbox
                name="data_value"
                value="data_value"
                checked={isChecked('data_value')}
                onChange={handleChange}
              >
                <Text variant="medium">Data value</Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl>
              <Checkbox
                name="notes"
                value="notes"
                checked={isChecked('notes')}
                onChange={handleChange}
              >
                <Text variant="medium">Notes</Text>
              </Checkbox>
            </FormControl>
          </li>
        </ul>
      </div>
    </section>
  );
};

export default memo(OutcomesPanel);
